import { NextComponentType } from 'next';
import { AppInitialProps, AppProps } from 'next/app';
import getConfig from 'next/config';
import React, { useEffect } from 'react';
import '../styles/custom-google-search.scss';

import { ThemeName, ThemeNames } from '@sravni/design-system-theme';
import { DeviceInfo } from '@sravni/koa-utils/lib/middlewares/device';
import { CommonHead } from '@sravni/next-common-head';
import { Footer } from '@sravni/react-footer';
import { DeviceInfoProvider, ThemeProvider } from '@sravni/react-utils';
import { isProduction } from '@sravni/server/utils/env';
import { Interceptor } from '@sravni/ugc/lib/utils/sentry/Interceptor';
import { formatUserData } from '@sravni/utils/lib/auth';

import { Header } from '../components/Header';
import Seo from '../components/Seo';
import { TopBanner } from '../components/TopBanner';
import StoreProvider from '../providers/store';
import { findRegionalCenters } from '../services/locations';
import { getOrCreateStore, IGlobalState } from '../store';
import { setCurrentLocation, setLocations } from '../store/reducers/locations';
import { setMetadata } from '../store/reducers/metadata';
import { setNews } from '../store/reducers/news';
import { setRatings } from '../store/reducers/ratings';
import { setSettings } from '../store/reducers/sitesettings';
import { setUser } from '../store/reducers/user';
import '@sravni/design-system-theme/lib/globalStyles.css';
import styles from '../styles/page.module.scss';

export const AB_NAME = 'AB_HOME';

interface IProps extends AppProps {
  realPath: string;
  initialReduxState: IGlobalState;
  deviceInfo: DeviceInfo;
  theme: ThemeNames;
}

const MyApp: NextComponentType<App.ReduxAppContext, AppInitialProps, IProps> = (props) => {
  const { Component, pageProps, initialReduxState, deviceInfo, theme } = props;
  const reduxStore = getOrCreateStore(initialReduxState);
  const { getState, dispatch } = reduxStore;
  const { user } = getState();
  const { publicRuntimeConfig } = getConfig();
  const { environment } = publicRuntimeConfig;
  const subId = user.loggedIn ? user.account?.sub : undefined;

  const fetchRegions = React.useCallback(async () => {
    let centers;

    const centersData = localStorage.getItem('centers');

    if (!centersData) {
      try {
        centers = await findRegionalCenters();

        localStorage.setItem('centers', JSON.stringify(centers));
      } catch (e) {}
    } else {
      try {
        centers = JSON.parse(centersData);
      } catch (e) {}
    }

    dispatch(setLocations(centers));
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { default: queue } = require('../utils/queue');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Sentry = require('@sentry/browser');

    if (isProduction) {
      queue.push(() => {
        Sentry.init({
          dsn: publicRuntimeConfig.sentryDSN,
          release: publicRuntimeConfig.release,
          environment,
        });
      });

      queue.push(() => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.register('/service-worker.js').catch((err) => {
            console.warn('service worker registration failed', err.message);
          });
        }
      });
    }

    queue.push(() => fetchRegions());
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  return (
    <Interceptor componentName="home-root-component">
      <StoreProvider store={reduxStore}>
        <ThemeProvider initialTheme={theme}>
          <DeviceInfoProvider initialInfo={deviceInfo}>
            <>
              <CommonHead
                sravniTMOptions={{
                  enabled: isProduction,
                  googleTmId: 'GTM-PMDFG9',
                  ab: AB_NAME,
                  userId: subId,
                }}
              />
              <Seo />
              <div className={styles['wrapper']}>
                <TopBanner />
                <Header />
                <Component {...pageProps} />
              </div>
              <Footer />
            </>
          </DeviceInfoProvider>
        </ThemeProvider>
      </StoreProvider>
    </Interceptor>
  );
};

MyApp.getInitialProps = async ({ Component, ctx }: App.ReduxAppContext) => {
  let pageProps = {};
  let deviceInfo: DeviceInfo = { phone: false, tablet: false };
  let theme: ThemeNames = ThemeName.lager;

  const reduxStore = getOrCreateStore();
  ctx.store = reduxStore;

  if (typeof window === 'undefined') {
    if (ctx.req.__USER__) {
      reduxStore.dispatch(setUser(formatUserData(ctx.req.__USER__)));
    }
    if (ctx.req.__SITE_SETTINGS__) {
      reduxStore.dispatch(setSettings(ctx.req.__SITE_SETTINGS__));
    }
    if (ctx.req.__SELECTED_LOCATION__) {
      reduxStore.dispatch(setCurrentLocation(ctx.req.__SELECTED_LOCATION__));
    }
    if (ctx.req.__SEO__) {
      reduxStore.dispatch(setMetadata(ctx.req.__SEO__));
    }
    if (ctx.req.__RATINGS__) {
      reduxStore.dispatch(setRatings(ctx.req.__RATINGS__));
    }
    if (ctx.req.__NEWS_HIGHLIGHTS__) {
      reduxStore.dispatch(setNews(ctx.req.__NEWS_HIGHLIGHTS__));
    }
  }

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  if (ctx.req && ctx.req.__DEVICE_INFO__) {
    deviceInfo = ctx.req.__DEVICE_INFO__;
  }
  if (ctx.req && ctx.req.__THEME__) {
    theme = ctx.req.__THEME__;
  }

  return {
    pageProps,
    initialReduxState: reduxStore.getState(),
    deviceInfo,
    realPath: ctx.req?.url || '',
    theme,
  };
};

// export function reportWebVitals(metric: NextWebVitalsMetric) {
//   console.log(metric)
// }

export default MyApp;
